import { Section } from '@components/index'
import { SectionTitle } from '@components/section/SectionTitle'
import { useSendLeaseReminder } from '@services/dashboard/mutations/useSendLeaseReminder'
import type {
  EndingLease,
  EndingLeases,
  EndingLeaseState,
  LeaseReminder
} from '@services/dashboard/queries/useEndingLeases'
import { format, parseISO } from 'date-fns'
import type { TFunction } from 'i18next'
import {
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Minus,
  Send
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useConfirm } from '@/components/ui/confirm-dialog'
import { cn } from '@/lib/utils'

interface EndingLeasesTableProps {
  data?: EndingLeases
  loading?: boolean
}

type Filter = 'ALL' | EndingLeaseState

const PAGE_SIZE = 10
const TH =
  'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
const TD = 'px-4 py-3.5 align-middle text-sm text-ink'

const STATE_TONE: Record<EndingLeaseState, string> = {
  EXTEND: 'bg-primary/10 text-primary',
  NOT_EXTEND: 'bg-peach/30 text-warning',
  NO_ANSWER: 'bg-ink/[0.07] text-ink/60',
  NOTICE: 'bg-destructive/10 text-destructive'
}

const FILTERS: { key: Filter; count: (d?: EndingLeases) => number }[] = [
  { key: 'ALL', count: (d) => d?.total ?? 0 },
  { key: 'NO_ANSWER', count: (d) => d?.no_answer ?? 0 },
  { key: 'EXTEND', count: (d) => d?.extend ?? 0 },
  { key: 'NOT_EXTEND', count: (d) => d?.not_extend ?? 0 },
  { key: 'NOTICE', count: (d) => d?.notice ?? 0 }
]

function reminderLook(status: LeaseReminder['status']) {
  switch (status) {
    case 'SENT':
      return { tone: 'bg-primary/10 text-primary', Icon: Check }
    case 'PENDING':
    case 'UPCOMING':
      return { tone: 'bg-ink/[0.06] text-ink/60', Icon: Clock }
    case 'SKIPPED':
    case 'CANCELED':
    case 'SUPERSEDED':
      return { tone: 'bg-ink/[0.06] text-ink/50', Icon: Minus }
    default: // MISSED, FAILED
      return { tone: 'bg-destructive/10 text-destructive', Icon: AlertTriangle }
  }
}

/** One "75 d" / "45 d" chip: did the tenant reminder for that day go out? */
function ReminderChip({
  reminder,
  label,
  t
}: {
  reminder: LeaseReminder
  label?: string
  t: TFunction
}) {
  const date = reminder.sent_at
    ? format(parseISO(reminder.sent_at), 'dd.MM.')
    : ''
  const { tone, Icon } = reminderLook(reminder.status)
  const title =
    reminder.status === 'SKIPPED' && reminder.reason
      ? t(`endingLeases.reminder.SKIPPED_${reminder.reason}`)
      : t(`endingLeases.reminder.${reminder.status}`, { date })
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold',
        tone
      )}
    >
      <Icon className="h-3 w-3" />
      {label ?? `${reminder.days} d`}
      {date && ` · ${date}`}
    </span>
  )
}

/**
 * Leases ending within 75 days and where each one stands: what the main
 * tenant answered in the app (extend / don't extend), no answer yet, or
 * already under notice. A row opens the offer, where the lease is extended
 * or ended.
 */
export function EndingLeasesTable({
  data,
  loading = false
}: EndingLeasesTableProps) {
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const [filter, setFilter] = useState<Filter>('ALL')
  const [page, setPage] = useState(0)
  const { confirm, dialog } = useConfirm()
  const { mutate: sendReminder, isPending: sending } = useSendLeaseReminder()

  // The backend decides what goes next: the latest due reminder that has not
  // gone out, then one manual reminder, then nothing.
  const sendLabel = (lease: EndingLease) =>
    t(`endingLeases.sendNext.${lease.next_reminder}`)

  const onSend = async (lease: EndingLease) => {
    const ok = await confirm({
      title: sendLabel(lease),
      description: t('endingLeases.sendConfirmText', {
        name: lease.tenant_name || lease.tenant_email
      }),
      confirmText: t('endingLeases.sendConfirm'),
      cancelText: t('endingLeases.cancel')
    })
    if (ok) sendReminder(lease.contract_id)
  }

  const leases = (data?.leases ?? []).filter(
    (lease) => filter === 'ALL' || lease.state === filter
  )
  const pages = Math.ceil(leases.length / PAGE_SIZE)
  const rows = leases.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)

  const choose = (next: Filter) => {
    setFilter(next)
    setPage(0)
  }

  return (
    <Section
      title={
        <SectionTitle
          icon={<CalendarClock />}
          title={t('endingLeases.title')}
        />
      }
      defaultActiveKey={['0']}
    >
      {loading ? (
        <div className="h-24 animate-pulse rounded-xl bg-ink/[0.04]" />
      ) : !data?.total ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {t('endingLeases.empty')}
        </p>
      ) : (
        <div className="space-y-4">
          {data.missed > 0 && (
            <div className="flex items-start gap-2 rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {t('endingLeases.missedWarning', { count: data.missed })}
              </span>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {FILTERS.map(({ key, count }) => (
              <button
                key={key}
                type="button"
                onClick={() => choose(key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border-0 px-3 py-1.5 text-xs font-semibold transition-colors',
                  filter === key
                    ? 'bg-ink text-white'
                    : 'bg-ink/[0.05] text-ink/70 hover:bg-ink/[0.09]'
                )}
              >
                {t(`endingLeases.filter.${key}`)}
                <span
                  className={cn(
                    'rounded-full px-1.5 text-[11px]',
                    filter === key ? 'bg-white/20' : 'bg-white'
                  )}
                >
                  {count(data)}
                </span>
              </button>
            ))}
          </div>

          <div className="-mx-5 -mb-5">
            {rows.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t('endingLeases.emptyFilter')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-y border-ink/[0.08] bg-ink/[0.02] text-left">
                      <th className={TH}>{t('endingLeases.end')}</th>
                      <th className={TH}>{t('endingLeases.flat')}</th>
                      <th className={TH}>{t('endingLeases.tenant')}</th>
                      <th className={TH}>{t('endingLeases.state')}</th>
                      <th className={TH}>{t('endingLeases.reminders')}</th>
                      <th className={TH}>{t('endingLeases.note')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((lease: EndingLease) => (
                      <tr
                        key={lease.contract_id}
                        onClick={() => navigate(`/admin/offers/${lease.offer_id}`)}
                        className="cursor-pointer border-b border-ink/[0.05] transition-colors last:border-0 hover:bg-ink/[0.015]"
                      >
                        <td className={cn(TD, 'whitespace-nowrap')}>
                          <div className="font-medium text-ink">
                            {format(parseISO(lease.end_date), 'dd.MM.yyyy')}
                          </div>
                          <div
                            className={cn(
                              'text-xs',
                              lease.days_left <= 30
                                ? 'font-semibold text-warning'
                                : 'text-muted-foreground'
                            )}
                          >
                            {t('endingLeases.daysLeft', {
                              count: lease.days_left
                            })}
                          </div>
                        </td>
                        <td className={TD}>
                          <div className="font-medium text-ink">
                            {lease.flat_title}
                          </div>
                          {lease.flat_address && (
                            <div className="text-xs text-muted-foreground">
                              {lease.flat_address}
                            </div>
                          )}
                        </td>
                        <td className={TD}>
                          <div className="font-medium text-ink">
                            {lease.tenant_name || lease.tenant_email}
                          </div>
                          {lease.tenant_name && (
                            <div className="text-xs text-muted-foreground">
                              {lease.tenant_email}
                            </div>
                          )}
                        </td>
                        <td className={cn(TD, 'whitespace-nowrap')}>
                          <span
                            className={cn(
                              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                              STATE_TONE[lease.state]
                            )}
                          >
                            {t(`endingLeases.filter.${lease.state}`)}
                          </span>
                          {lease.renewal_answered_at && (
                            <div className="mt-1 text-xs text-muted-foreground">
                              {format(
                                parseISO(lease.renewal_answered_at),
                                'dd.MM.yyyy'
                              )}
                            </div>
                          )}
                        </td>
                        <td className={TD}>
                          <div className="flex flex-col items-start gap-1">
                            {lease.reminders.map((reminder) => (
                              <ReminderChip
                                key={reminder.days}
                                reminder={reminder}
                                t={t}
                              />
                            ))}
                            {lease.manual_reminder && (
                              <ReminderChip
                                reminder={{
                                  days: 0,
                                  status:
                                    lease.manual_reminder.status as LeaseReminder['status'],
                                  sent_at: lease.manual_reminder.sent_at,
                                  reason: ''
                                }}
                                label={t('endingLeases.manual')}
                                t={t}
                              />
                            )}
                            {lease.next_reminder && (
                              <button
                                type="button"
                                disabled={sending}
                                onClick={(event) => {
                                  // The row itself opens the offer.
                                  event.stopPropagation()
                                  onSend(lease)
                                }}
                                className="mt-0.5 inline-flex items-center gap-1 whitespace-nowrap rounded-full border-0 bg-primary px-2.5 py-1 text-[11px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                              >
                                <Send className="h-3 w-3" />
                                {sendLabel(lease)}
                              </button>
                            )}
                          </div>
                        </td>
                        <td
                          className={cn(TD, 'max-w-[240px] text-muted-foreground')}
                        >
                          <span className="line-clamp-2" title={lease.renewal_note}>
                            {lease.renewal_note || '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pages > 1 && (
              <div className="flex items-center justify-end gap-1.5 px-4 py-3">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-sm font-medium text-muted-foreground">
                  {page + 1} / {pages}
                </span>
                <button
                  type="button"
                  disabled={page === pages - 1}
                  onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
                  className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {dialog}
    </Section>
  )
}
